import { formatValueWithStep, roundToPrecision } from '@pages/Trade/utils/helpers'

import { ProtectiveOrderType, ProtectiveOrderTypes } from '@model/DxTrade.types'

import type { PortfolioTrade } from '../hooks/useDxtradePortfolioTrades'
import type { PendingOrderWithLots } from '../hooks/usePendingOrdersWithLots'
import { getOpenLegTargetPrice } from './formatters'
import { calculatePnL } from './functions'

export type ProtectiveOrderValidationParams = {
  type: ProtectiveOrderType
  price: number | ''
  currentPrice?: number
  baseSide?: string | null
  isActive: boolean
}

export type ProtectiveOrderValidationResult = {
  isError: boolean
  referencePrice: number
  shouldBeAbove: boolean
}

export const isNumber = (value: unknown): value is number =>
  typeof value === 'number' && !Number.isNaN(value)

export const parsePriceInputValue = (value: string): number | '' =>
  value === '' ? '' : Number(value)

export const formatPriceForInstrument = (
  price: number | '',
  meta?: { priceIncrement?: number | null }
): string | '' => {
  if (price === '' || typeof price !== 'number') return price
  const step = meta?.priceIncrement ?? undefined
  return formatValueWithStep(price, step)
}

export const getProtectiveSign = (
  type: ProtectiveOrderType,
  side?: string | null
): 1 | -1 | null => {
  if (side !== 'BUY' && side !== 'SELL') return null
  const isTp = type === ProtectiveOrderTypes.takeProfit
  return isTp ? (side === 'BUY' ? 1 : -1) : side === 'BUY' ? -1 : 1
}

export const priceFromPips = ({
  basePrice,
  pipSize,
  pips,
  sign,
  priceIncrement,
}: {
  basePrice?: number | null
  pipSize?: number | null
  pips?: number | null
  sign: 1 | -1 | null
  priceIncrement?: number | null
}): number | null => {
  if (!isNumber(basePrice) || !pipSize || !isNumber(pips) || !sign) return null
  const raw = basePrice + sign * pipSize * pips
  return roundToIncrement(raw, priceIncrement ?? undefined)
}

export const pipsFromPrice = ({
  price,
  basePrice,
  pipSize,
  sign,
}: {
  price?: number | null
  basePrice?: number | null
  pipSize?: number | null
  sign: 1 | -1 | null
}): number | null => {
  if (!isNumber(price) || !isNumber(basePrice) || !pipSize || !sign) return null
  return (price - basePrice) / (sign * pipSize)
}

export const getProtectiveBasePrice = ({
  currentPrice,
}: {
  currentPrice?: number | null
}): number | null => {
  if (isNumber(currentPrice)) return currentPrice
  return null
}

export const getDefaultProtectivePrice = ({
  type,
  isActive,
  prevPrice,
  basePrice,
  pipSize,
  baseSide,
}: {
  type: ProtectiveOrderType
  isActive: boolean
  prevPrice: number | ''
  basePrice?: number | null
  pipSize?: number | null
  baseSide?: string | null
}): number | '' => {
  if (!isActive) return prevPrice
  if (prevPrice !== '' && isNumber(prevPrice)) return prevPrice
  if (!isNumber(basePrice) || !pipSize) return prevPrice
  const sign = getProtectiveSign(type, baseSide)
  if (!sign) return prevPrice

  return basePrice + sign * 10 * pipSize
}

export const getProtectiveOrderValidation = ({
  type,
  price,
  currentPrice,
  baseSide,
  isActive,
}: ProtectiveOrderValidationParams): ProtectiveOrderValidationResult | undefined => {
  if (!isActive) return undefined
  if (!isNumber(price)) return undefined
  if (!isNumber(currentPrice)) return undefined

  const isTp = type === ProtectiveOrderTypes.takeProfit
  const shouldBeAbove = (isTp && baseSide === 'BUY') || (!isTp && baseSide === 'SELL')
  const violatesAbove = shouldBeAbove && price <= currentPrice
  const violatesBelow = !shouldBeAbove && price >= currentPrice

  if (violatesAbove || violatesBelow) {
    return { isError: true, referencePrice: currentPrice, shouldBeAbove }
  }

  return undefined
}

export type ProtectiveOrderContext = {
  position?: PortfolioTrade
  order?: PendingOrderWithLots
}

export const getOpenPriceAndQuantity = ({
  position,
  order,
}: ProtectiveOrderContext): {
  openPrice?: number | null
  quantity?: number | null
  side?: string | null
} => {
  const side = position?.side ?? order?.side ?? null
  const targetPrice = order ? getOpenLegTargetPrice(order) : undefined
  const openPrice = position?.entryPrice ?? targetPrice ?? null
  const quantity = position?.quantity ?? order?.legs?.[0]?.quantity ?? null
  return { openPrice, quantity, side }
}

const roundToIncrement = (value: number, increment?: number | null): number => {
  if (!increment || increment <= 0) return value
  return roundToPrecision(Math.round(value / increment) * increment, increment)
}

export const priceToAmount = ({
  side,
  openPrice,
  quantity,
  exitPrice,
}: {
  side?: string | null
  openPrice?: number | null
  quantity?: number | null
  exitPrice?: number | null
}): number | null => {
  return calculatePnL({ side, openPrice, quantity, exitPrice })
}

export const amountToPrice = ({
  side,
  openPrice,
  quantity,
  amount,
  priceIncrement,
}: {
  side?: string | null
  openPrice?: number | null
  quantity?: number | null
  amount?: number | null
  priceIncrement?: number | null
}): number | null => {
  if (!isNumber(amount) || !isNumber(openPrice) || !isNumber(quantity) || quantity === 0) {
    return null
  }
  if (side !== 'BUY' && side !== 'SELL') {
    return null
  }
  const delta = amount / quantity
  const rawPrice = side === 'BUY' ? openPrice + delta : openPrice - delta
  const increment = priceIncrement ?? null
  return roundToIncrement(rawPrice, increment ?? undefined)
}
